import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";
import { cn } from "../../lib/cn.ts";

const buttonVariants = cva(
  "inline-flex shrink-0 select-none items-center justify-center gap-2 rounded-md text-button whitespace-nowrap transition-[color,background-color,border-color,box-shadow,opacity,translate,scale] duration-(--duration-press) ease-(--ease-out-strong) outline-none active:scale-[0.97] active:duration-75 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-brand-hover",
        destructive: "bg-destructive text-white hover:bg-destructive/85",
        // DESIGN.md `button-secondary`: charcoal fill, 1px hairline, no shadow.
        outline: "border border-hairline bg-surface-1 text-ink hover:bg-surface-2",
        secondary: "bg-surface-2 text-ink hover:bg-surface-3",
        // DESIGN.md `button-tertiary`: plain text on the canvas until it is hovered.
        ghost: "text-ink hover:bg-surface-2",
        // Text in a sentence, not an object: scaling it on press would move the words around it.
        link: "text-brand-strong underline-offset-4 hover:underline active:scale-100",
      },
      size: {
        default: "h-10 px-3.5 py-2 has-[>svg]:px-3",
        xs: "h-7 gap-1 rounded-sm px-2 text-caption has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-9 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-12 px-6 text-body has-[>svg]:px-4",
        icon: "size-10",
        "icon-xs": "size-7 rounded-sm [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-9",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
